import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../ui/button";
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../ui/card";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { useApi } from "../../lib/ApiContext";
import { useAuth } from "../../lib/AuthContext";

const OrganizationsIndex = () => {
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const api = useApi();
    const { user } = useAuth() || {};

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const data = await api.organizations.getAll();
                setOrganizations(data);
                setLoading(false);
            } catch (err) {
                console.error("Failed to fetch organizations:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchOrganizations();
    }, [api.organizations]);

    const handleRowClick = (orgPublicId) => {
        navigate(`/organizations/${orgPublicId}/edit`);
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        Failed to load organizations: {error}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div>
            <div className="container mx-auto p-4">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">Organizations</h1>
                    {user?.is_super_admin && (
                        <Button onClick={() => navigate('/organizations/new')}>
                            Add Organization
                        </Button>
                    )}
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>My Organizations</CardTitle>
                        <CardDescription>
                            Organizations you have access to manage and configure.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableCaption>Organizations you have access to in the system.</TableCaption>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Custom Domain</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {organizations.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                                            No organizations found. Click "Add Organization" to create one.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    organizations.map((org) => (
                                        <TableRow
                                            key={org.id}
                                            onClick={() => handleRowClick(org.public_id)}
                                            className="cursor-pointer hover:bg-gray-100"
                                        >
                                            <TableCell className="font-medium">{org.name}</TableCell>
                                            <TableCell>
                                                {org.custom_domain ? (
                                                    <a
                                                        href={org.custom_domain}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {org.custom_domain}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">No custom domain</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${org.active !== false ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                    {org.active !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default OrganizationsIndex; 